// Chat — main work surface. Wider layout. No per-message cost chip in default mode.
// Asset menu in the thread header. Model picker handled by ChatInput.

import { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../api';
import type { AssetType, Brain, Message, ModelChoice, Settings, Thread, ThreadAsset } from '../types';
import { ElementCell } from '../components/ElementCell';
import { ChatInput } from '../components/ChatInput';
import { Markdown } from '../components/Markdown';
import { AuditTrail } from '../components/AuditTrail';
import { AssetMenu } from '../components/AssetMenu';
import { BrainPanel } from '../components/BrainPanel';

interface Props {
  thread: Thread;
  allBrains: Brain[];
  settings: Settings;
  greetingName?: string;
  brainPillOpen: boolean;
  onBrainPillToggle: () => void;
  onBrainPanelChange: (newBrainIds: string[]) => Promise<void>;
  onBrainPanelClose: () => void;
  onCostBump: (cents: number) => void;
  onThreadUpdate: (thread: Thread) => void;
  onAssetGenerated: (asset: ThreadAsset) => void;
  onAssetGeneratingChange: (type: AssetType | null) => void;
  onAssetError: (msg: string) => void;
}

interface StreamingState {
  active: boolean;
  partialText: string;
  brainIdsUsed: string[];
  error?: string;
  orchestrationPhase: 'idle' | 'thinking' | 'selected' | 'generating';
  orchestrationModel: string | null;
  orchestrationReason: string;
}

const EXAMPLES = [
  { tag: 'Process',  symbol: 'Sc', atomicNumber: 21, text: 'Compare extraction routes for scandium from bauxite residue at Higgins I scale.' },
  { tag: 'Strategy', symbol: 'Ga', atomicNumber: 31, text: 'What should our pilot prioritize next quarter given the Maaden findings?' },
  { tag: 'Sourcing', symbol: 'Ge', atomicNumber: 32, text: 'Where can we domestically source 5 tonnes/yr of germanium for a defense-sensor program, ranked by supply risk?' },
];

export function Chat({
  thread, allBrains, settings, greetingName,
  brainPillOpen, onBrainPillToggle, onBrainPanelChange, onBrainPanelClose,
  onCostBump, onThreadUpdate,
  onAssetGenerated, onAssetGeneratingChange, onAssetError,
}: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState<StreamingState>({
    active: false,
    partialText: '',
    brainIdsUsed: [],
    orchestrationPhase: 'idle',
    orchestrationModel: null,
    orchestrationReason: '',
  });
  const [lens, setLensRaw] = useState<'objective' | 'lens'>(() => readLensForThread(thread));
  const setLens = (next: 'objective' | 'lens') => {
    setLensRaw(next);
    try {
      localStorage.setItem(`element-iq:lens:${thread.id}`, next);
    } catch {
      // localStorage may be unavailable (private mode, quota); fail silent
    }
  };
  const [model, setModel] = useState<ModelChoice>('auto');
  // When the user clicks into the empty-state hero input, we shift the layout
  // toward what they'll see post-submit: input docks to the bottom, suggestions
  // fade out, hero text recedes. Smooth UX bridge between empty and active.
  const [heroFocused, setHeroFocused] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Auto-scroll respects the user. Once they scroll up, we stop forcing them
  // to the bottom until they return there themselves.
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  // Abort controller for the active stream — powers the Stop button.
  const abortRef = useRef<AbortController | null>(null);
  // ID of the user message currently being edited inline, plus its draft text.
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  // Load existing messages on thread change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getThreadMessages(thread.id).then((msgs) => {
      if (cancelled) return;
      setMessages(msgs);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [thread.id]);

  // Auto-scroll respects user intent: if they scrolled up, we stop forcing
  // them down. Reactivates only when they return to the bottom themselves.
  useEffect(() => {
    if (!autoScrollEnabled) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, streaming.partialText, autoScrollEnabled]);

  // Direction-based scroll tracking. Any deliberate upward scroll pauses auto-
  // scroll immediately. Auto-scroll only re-enables when the user actually
  // returns to the bottom (within 60px). Lets the user free-scroll through the
  // thread while the agent is still streaming without being yanked down.
  useEffect(() => {
    let lastScrollY = window.scrollY;
    function onScroll() {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY;
      lastScrollY = currentScrollY;
      // Ignore sub-pixel jitter (prevents momentum/inertia scrolls from toggling)
      if (Math.abs(delta) < 3) return;
      if (delta < 0) {
        // Upward scroll — pause auto-scroll
        setAutoScrollEnabled((prev) => (prev ? false : prev));
        return;
      }
      // Downward scroll — re-enable only if user has actually returned to bottom
      const distanceFromBottom =
        document.documentElement.scrollHeight - window.innerHeight - currentScrollY;
      if (distanceFromBottom <= 60) {
        setAutoScrollEnabled((prev) => (prev ? prev : true));
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Reset to enabled when the user opens a different thread + re-read lens
  useEffect(() => {
    setAutoScrollEnabled(true);
    setLensRaw(readLensForThread(thread));
    setEditingMessageId(null);
  }, [thread.id]);

  const brainsForUi = useMemo(
    () => thread.brain_ids.map((id) => allBrains.find((b) => b.id === id)).filter((b): b is Brain => !!b),
    [thread.brain_ids, allBrains],
  );

  async function sendPrompt(prompt: string) {
    if (streaming.active) return;
    // Sending a new prompt re-enables auto-scroll so the user sees their
    // own message + the start of the response.
    setAutoScrollEnabled(true);
    const tempUserMsg: Message = {
      id: `temp_${Date.now()}`,
      thread_id: thread.id,
      role: 'user',
      content: prompt,
      brain_ids_used: null,
      model_used: null,
      lens_used: null,
      input_tokens: 0,
      output_tokens: 0,
      cache_read_tokens: 0,
      cache_create_tokens: 0,
      cost_cents: 0,
      duration_ms: 0,
      created_at: Math.floor(Date.now() / 1000),
    };
    setMessages((m) => [...m, tempUserMsg]);
    setStreaming({
      active: true,
      partialText: '',
      brainIdsUsed: [],
      orchestrationPhase: 'thinking',
      orchestrationModel: null,
      orchestrationReason: '',
    });

    const modelOverride = model === 'auto' ? undefined : model;
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      for await (const event of api.chatStream(thread.id, prompt, lens, modelOverride, controller.signal)) {
        if (event.type === 'orchestrating') {
          const data = event.data;
          if (data.phase === 'thinking') {
            setStreaming((s) => ({ ...s, orchestrationPhase: 'thinking' }));
          } else {
            const brainIds = data.brain_ids;
            const modelChoice = data.model;
            const reasonText = data.reason;
            setStreaming((s) => ({
              ...s,
              orchestrationPhase: 'selected',
              brainIdsUsed: brainIds,
              orchestrationModel: modelChoice,
              orchestrationReason: reasonText,
            }));
            // Reflect orchestrated brains in the brain pill at the top of the chat
            onThreadUpdate({ ...thread, brain_ids: brainIds });
          }
        } else if (event.type === 'start') {
          setStreaming((s) => ({
            ...s,
            brainIdsUsed: event.data.brain_ids,
            orchestrationPhase: 'generating',
          }));
        } else if (event.type === 'text') {
          setStreaming((s) => ({ ...s, partialText: s.partialText + event.data.delta }));
        } else if (event.type === 'done') {
          const fresh = await api.getThreadMessages(thread.id);
          setMessages(fresh);
          setStreaming({ active: false, partialText: '', brainIdsUsed: [], orchestrationPhase: 'idle', orchestrationModel: null, orchestrationReason: '' });
          if (event.data.cost_cents) onCostBump(event.data.cost_cents);
          // Refresh thread (auto-title may have set title)
          api.getThread(thread.id).then(onThreadUpdate).catch(() => {});
        } else if (event.type === 'error') {
          setStreaming({ active: false, partialText: '', brainIdsUsed: [], error: event.data.message, orchestrationPhase: 'idle', orchestrationModel: null, orchestrationReason: '' });
          const fresh = await api.getThreadMessages(thread.id);
          setMessages(fresh);
        }
      }
    } catch (err: any) {
      // Abort is a normal user action, not an error worth surfacing
      const isAbort = err?.name === 'AbortError';
      setStreaming({
        active: false,
        partialText: '',
        brainIdsUsed: [],
        error: isAbort ? undefined : (err?.message ?? String(err)),
        orchestrationPhase: 'idle',
        orchestrationModel: null,
        orchestrationReason: '',
      });
      if (isAbort) {
        // Pull whatever made it to the DB so the UI shows the partial answer
        api.getThreadMessages(thread.id).then((msgs) => setMessages(msgs)).catch(() => {});
      }
    } finally {
      abortRef.current = null;
    }
  }

  function handleStopGenerating() {
    abortRef.current?.abort();
  }

  function handleStartEdit(msg: Message) {
    if (streaming.active) return;
    setEditingMessageId(msg.id);
    setEditDraft(msg.content);
  }

  function handleCancelEdit() {
    setEditingMessageId(null);
    setEditDraft('');
  }

  async function handleSubmitEdit(msg: Message) {
    const newPrompt = editDraft.trim();
    if (!newPrompt || streaming.active) return;
    setEditingMessageId(null);
    setEditDraft('');
    try {
      await api.truncateThreadAtMessage(thread.id, msg.id);
    } catch (err: any) {
      setStreaming((s) => ({ ...s, error: err?.message ?? 'Couldn\'t edit that message' }));
      return;
    }
    // Refresh the message list (now truncated), then re-fire the chat
    const fresh = await api.getThreadMessages(thread.id);
    setMessages(fresh);
    sendPrompt(newPrompt);
  }

  async function handleAssetSelect(type: AssetType) {
    onAssetGeneratingChange(type);
    try {
      const result = await api.generateAsset(thread.id, type);
      onAssetGenerated(result);
      onCostBump(result.cost_cents);
    } catch (err: any) {
      onAssetError(err?.message ?? String(err));
    }
  }

  const isEmpty = messages.length === 0 && !streaming.active;
  const canSynthesize = messages.some((m) => m.role === 'assistant') && !streaming.active;

  if (loading) {
    return (
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 md:px-10 pt-12 pb-48">
        <div className="space-y-5">
          <SkeletonCard variant="user" />
          <SkeletonCard variant="assistant" />
          <SkeletonCard variant="user" widthPct={70} />
        </div>
      </main>
    );
  }

  // Shared chat-content header — brain pill (left), title + asset menu (right).
  // One-row layout in both empty and conversation states; the title truncates
  // in available space rather than wrapping the row.
  const chatHeaderBar = (
    <div className="sticky top-[57px] z-30 bg-bg/90 backdrop-blur-md border-b border-hairline">
    <div className="relative max-w-[1100px] mx-auto px-4 sm:px-6 md:px-10 pt-4 md:pt-5 pb-3">
      <div className="flex justify-between items-center gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            onClick={onBrainPillToggle}
            className={`relative inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all duration-200 flex-shrink-0 ${
              brainPillOpen
                ? 'bg-ink text-bg border border-ink shadow-sm'
                : 'bg-surface text-ink border border-border hover:border-ink hover:-translate-y-px hover:shadow-sm'
            }`}
          >
            <span className="relative inline-grid place-items-center w-2.5 h-2.5">
              <span className="absolute inset-[-2px] rounded-full border border-accent opacity-40 animate-pulse" />
              <span className="w-1.5 h-1.5 bg-accent rounded-full" />
            </span>
            {thread.brain_ids.length} brain{thread.brain_ids.length === 1 ? '' : 's'} loaded
          </button>
          {!isEmpty && (
            <span className="text-ink/80 text-[13px] font-medium truncate min-w-0">
              {thread.title ?? 'New chat'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isEmpty && (
            <span className="font-mono text-[11px] text-text3 hidden lg:inline">
              {messages.length} message{messages.length === 1 ? '' : 's'}
            </span>
          )}
          <AssetMenu disabled={!canSynthesize} onSelect={handleAssetSelect} />
        </div>
      </div>
      {brainPillOpen && (
        <BrainPanel
          thread={thread}
          allBrains={allBrains}
          onChange={onBrainPanelChange}
          onClose={onBrainPanelClose}
        />
      )}
    </div>
    </div>
  );

  // ─── EMPTY STATE — hero centered ─────────────────────────────────
  if (isEmpty) {
    return (
      <>
        {chatHeaderBar}
        <main className="max-w-[1100px] mx-auto px-4 sm:px-6 md:px-10 pt-10 sm:pt-14 md:pt-16 pb-56">
          <div
            className={`transition-all duration-400 ease-out ${
              heroFocused ? 'opacity-50 -translate-y-1' : 'opacity-100 translate-y-0'
            }`}
          >
            <div className="text-text2 text-[13px] font-medium mb-3.5 inline-flex items-center gap-2.5">
              <span className="w-1.5 h-1.5 bg-ink rounded-full" />
              {(() => {
                const h = new Date().getHours();
                const tod = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
                return greetingName ? `Good ${tod}, ${greetingName}.` : `Good ${tod}.`;
              })()}{' '}
              {thread.brain_ids.length > 0 && `${thread.brain_ids.length} brain${thread.brain_ids.length === 1 ? '' : 's'} loaded.`}
            </div>
            <h1 className="font-display text-[28px] sm:text-[34px] md:text-[40px] leading-[1.1] text-ink tracking-tight25 mb-8 sm:mb-12 max-w-[760px]">
              What can I help you think through about <span className="text-accent">critical minerals?</span>
            </h1>
          </div>

          {/* Hero input — smoothly slides to dock position when focused */}
          <div
            className={`transition-all duration-400 ease-out ${
              heroFocused
                ? 'fixed bottom-0 left-0 md:left-64 right-0 lg:right-72 z-[5] bg-bg/85 backdrop-blur-md border-t border-hairline px-4 sm:px-6 md:px-10 py-3 md:py-4 pb-4 md:pb-5'
                : ''
            }`}
          >
            <ChatInput
              variant={heroFocused ? 'dock' : 'hero'}
              lens={lens}
              onLensChange={setLens}
              model={model}
              onModelChange={setModel}
              onSubmit={sendPrompt}
              onFocus={() => setHeroFocused(true)}
              onBlur={(v) => { if (!v.trim()) setHeroFocused(false); }}
              placeholder="What's the most cost-effective extraction route for scandium from bauxite residue?"
              brainCount={thread.brain_ids.length || undefined}
            />
          </div>

          {/* Suggestions — greyed at rest so they don't compete with the input;
              fade out completely once the user has clicked into the chat box. */}
          <div
            className={`transition-all duration-400 ease-out ${
              heroFocused ? 'opacity-0 pointer-events-none -translate-y-2' : 'opacity-60 hover:opacity-100'
            }`}
          >
            <div className="text-text2 text-[12px] mt-12 mb-3.5">Or try one of these</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex.text}
                  onClick={() => sendPrompt(ex.text)}
                  className="text-left group bg-card border border-border/70 rounded-md p-4 cursor-pointer transition-all hover:border-ink/40 hover:-translate-y-0.5 hover:shadow-sm font-sans text-[13px] text-text2 leading-snug relative overflow-hidden"
                >
                  <span className="absolute left-0 top-0 w-[3px] h-full bg-ink opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="inline-flex items-center gap-2 mb-3">
                    <ElementCell symbol={ex.symbol} atomicNumber={ex.atomicNumber} size="sm" />
                    <span className="text-[10px] text-text2 font-medium">{ex.tag}</span>
                  </div>
                  <div className="text-ink">{ex.text}</div>
                </button>
              ))}
            </div>
          </div>

          {streaming.error && <ErrorBanner message={streaming.error} />}
        </main>
      </>
    );
  }

  // ─── CONVERSATION ─────────────────────────────────────────────────
  return (
    <>
      {chatHeaderBar}
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 md:px-10 pt-7 pb-36">
        {messages.map((m) => (
          <MessageBlock
            key={m.id}
            message={m}
            brains={allBrains}
            advancedMode={settings.advanced_mode}
            isEditing={editingMessageId === m.id}
            editDraft={editDraft}
            editDisabled={streaming.active}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onChangeDraft={setEditDraft}
            onSubmitEdit={handleSubmitEdit}
          />
        ))}

        {streaming.active && (
          <StreamingBlock
            partialText={streaming.partialText}
            brains={brainsForUi}
            orchestrationPhase={streaming.orchestrationPhase}
            orchestrationModel={streaming.orchestrationModel}
            orchestrationReason={streaming.orchestrationReason}
          />
        )}

        {streaming.error && <ErrorBanner message={streaming.error} />}

        <div ref={messagesEndRef} className="h-1" style={{ scrollMarginBottom: '260px' }} />
      </main>

      {/* Stop generating — visible only while streaming */}
      {streaming.active && (
        <button
          onClick={handleStopGenerating}
          className="fixed bottom-32 right-4 lg:right-[22rem] z-[6] bg-card text-ink text-[12px] font-medium border border-border rounded-full px-3.5 py-1.5 shadow-md hover:border-accent hover:text-accent transition-colors inline-flex items-center gap-1.5"
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="1.5" />
          </svg>
          Stop
        </button>
      )}

      {/* "Jump to latest" — shown only when user has scrolled away during streaming */}
      {!autoScrollEnabled && streaming.active && (
        <button
          onClick={() => {
            setAutoScrollEnabled(true);
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }}
          className="fixed bottom-32 left-1/2 -translate-x-1/2 z-[6] bg-ink text-bg text-[12px] font-medium rounded-full px-3.5 py-1.5 shadow-md hover:bg-accent transition-colors inline-flex items-center gap-1.5"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
          Jump to latest
        </button>
      )}

      {/* Docked input — flanked by sidebar (left) and assets rail (right) on lg+.
          On md it spans from sidebar to viewport-right. On sm/mobile, full width. */}
      <div className="fixed bottom-0 left-0 md:left-64 right-0 lg:right-72 bg-bg/85 backdrop-blur-md border-t border-hairline px-4 sm:px-6 md:px-10 py-3 md:py-4 pb-4 md:pb-5 z-[5]">
        <ChatInput
          variant="dock"
          lens={lens}
          onLensChange={setLens}
          model={model}
          onModelChange={setModel}
          disabled={streaming.active}
          onSubmit={sendPrompt}
          placeholder={streaming.active ? 'Element IQ is responding…' : 'Ask a follow-up...'}
          brainCount={thread.brain_ids.length || undefined}
        />
      </div>
    </>
  );
}

interface MessageBlockProps {
  message: Message;
  brains: Brain[];
  advancedMode: boolean;
  isEditing: boolean;
  editDraft: string;
  editDisabled: boolean;
  onStartEdit: (msg: Message) => void;
  onCancelEdit: () => void;
  onChangeDraft: (text: string) => void;
  onSubmitEdit: (msg: Message) => void;
}

function MessageBlock({
  message, brains, advancedMode,
  isEditing, editDraft, editDisabled,
  onStartEdit, onCancelEdit, onChangeDraft, onSubmitEdit,
}: MessageBlockProps) {
  if (message.role === 'user') {
    if (isEditing) {
      return (
        <div className="bg-card border border-ink/40 rounded-lg px-5 py-4 mb-6 text-[15px] text-ink shadow-sm">
          <div className="flex justify-between items-center mb-2">
            <span className="text-[11px] text-text2 font-medium">Editing your message</span>
            <span className="font-mono text-[10px] text-text3">↵ to save · esc to cancel</span>
          </div>
          <textarea
            value={editDraft}
            onChange={(e) => onChangeDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmitEdit(message);
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancelEdit();
              }
            }}
            autoFocus
            rows={3}
            className="w-full bg-transparent border-none outline-none resize-none text-[15px] text-ink leading-relaxed font-sans"
          />
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-hairline">
            <button
              onClick={() => onSubmitEdit(message)}
              disabled={editDisabled || editDraft.trim().length === 0}
              className="bg-ink text-bg rounded-md px-3 py-1 text-[12px] font-medium hover:bg-accent transition-colors disabled:opacity-40"
            >
              Save and re-run
            </button>
            <button
              onClick={onCancelEdit}
              className="text-text2 text-[12px] hover:text-ink"
            >
              Cancel
            </button>
            <span className="text-[11px] text-text3 ml-auto">
              This will replace the answer below.
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="group bg-card border border-border/60 rounded-lg px-5 py-4 mb-6 text-[15px] text-ink relative">
        <div className="flex justify-between items-center mb-2">
          <span className="text-[11px] text-text3 font-medium">You</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onStartEdit(message)}
              disabled={editDisabled}
              title="Edit and re-run"
              className="text-text3 hover:text-ink opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </button>
            <span className="font-mono text-[10px] text-text3">{formatClockTime(message.created_at)}</span>
          </div>
        </div>
        <div className="leading-relaxed">{message.content}</div>
      </div>
    );
  }

  const brainsUsed = (message.brain_ids_used ?? [])
    .map((id) => brains.find((b) => b.id === id))
    .filter((b): b is Brain => !!b);

  return (
    <div className="mb-7">
      <div className="flex items-center gap-2.5 mb-3.5 flex-wrap">
        <span className="text-[12px] text-ink font-medium">Element IQ</span>
        {advancedMode && message.cost_cents > 0 && (
          <span className="font-mono text-[11px] text-text2 bg-card border border-hairline px-2.5 py-0.5 rounded-full">
            {message.cost_cents.toFixed(3)}¢ · {message.model_used?.replace('claude-', '')}
          </span>
        )}
      </div>
      <div className="bg-card border border-border/60 rounded-lg px-7 py-6">
        <Markdown text={message.content} brains={brainsUsed} />
      </div>
      {brainsUsed.length > 0 && (
        <AuditTrail brains={brainsUsed} costCents={advancedMode ? message.cost_cents : undefined} durationMs={advancedMode ? message.duration_ms : undefined} />
      )}
    </div>
  );
}

// StreamingMarkdown — lookahead buffer + paced row reveal, no animations.
//
// Two layers, both needed:
//
// 1. LOOKAHEAD: only content the model has moved at least 3 rows past is
//    eligible to display. Guarantees each row is fully assembled before
//    the user could ever see it — no partial cells, no mid-formation flashes.
//
// 2. PACED REVEAL: a self-rescheduling timer reveals ONE row every 250ms
//    from the eligible buffer. Even if the network delivers 10 rows at once,
//    the user sees them roll out at a steady deliberate pace. The smoothness
//    is the rhythm, not visual effects. When the stream ends, drain at 30ms
//    per row so the buffered rows flush quickly without a long tail.
//
// Rows just appear when their turn comes. No fade, no slide, no filter.
function StreamingMarkdown({ text, brains, isActive }: { text: string; brains: Brain[]; isActive: boolean }) {
  // LOOKAHEAD buffer — what's eligible to display.
  const target = useMemo(() => {
    if (!isActive) return text;
    const newlines: number[] = [];
    for (let i = 0; i < text.length; i++) {
      if (text[i] === '\n') newlines.push(i);
    }
    const LOOKAHEAD = 3;
    if (newlines.length <= LOOKAHEAD) return '';
    const cutoff = newlines[newlines.length - 1 - LOOKAHEAD];
    return text.slice(0, cutoff + 1);
  }, [text, isActive]);

  // PACED REVEAL state.
  const [displayedText, setDisplayedText] = useState('');
  const targetRef = useRef(target);
  const activeRef = useRef(isActive);
  useEffect(() => { targetRef.current = target; }, [target]);
  useEffect(() => { activeRef.current = isActive; }, [isActive]);

  // Reset displayed if target shrinks (new prompt, etc.)
  useEffect(() => {
    if (target.length < displayedText.length) {
      setDisplayedText(target);
    }
  }, [target, displayedText.length]);

  // Self-rescheduling timer. Reveals one full row per tick at a cadence
  // determined by current isActive.
  useEffect(() => {
    let timeoutId: number | null = null;
    let cancelled = false;
    const ACTIVE_DELAY_MS = 250; // ~4 rows/sec while streaming
    const DRAIN_DELAY_MS = 30;   // ~33 rows/sec catch-up when stream ends

    function tick() {
      if (cancelled) return;
      setDisplayedText((prev) => {
        const t = targetRef.current;
        if (prev.length >= t.length) return prev;
        const nextNewline = t.indexOf('\n', prev.length);
        return nextNewline < 0 ? t : t.slice(0, nextNewline + 1);
      });
      const delay = activeRef.current ? ACTIVE_DELAY_MS : DRAIN_DELAY_MS;
      timeoutId = window.setTimeout(tick, delay);
    }
    tick();
    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, []);

  const blocks = useMemo(() => {
    return displayedText.split(/\n{2,}/).filter((b) => b.length > 0);
  }, [displayedText]);

  const showPending = isActive || displayedText.length < target.length;

  return (
    <div className="streaming-md">
      {blocks.map((block, i) => (
        <div key={i} className="streaming-block">
          <Markdown text={block} brains={brains} />
        </div>
      ))}
      {showPending && (
        <div className="flex items-center gap-2 mt-3 text-text3 text-[12px]">
          <span className="stream-caret" aria-hidden="true" />
          <span className="opacity-70">writing…</span>
        </div>
      )}
    </div>
  );
}

function StreamingBlock({
  partialText, brains, orchestrationPhase, orchestrationModel, orchestrationReason,
}: {
  partialText: string;
  brains: Brain[];
  orchestrationPhase: 'idle' | 'thinking' | 'selected' | 'generating';
  orchestrationModel: string | null;
  orchestrationReason: string;
}) {
  const modelShort = orchestrationModel?.replace('claude-', '').replace('-4-', ' 4.').replace(/-(\d)$/, ' $1') ?? null;

  return (
    <div className="mb-7">
      <div className="flex items-center gap-2.5 mb-3.5 flex-wrap">
        <span className="text-[12px] text-ink font-medium">Element IQ</span>
        <OrchestrationIndicator
          phase={orchestrationPhase}
          brainCount={brains.length}
          modelShort={modelShort}
          reason={orchestrationReason}
        />
      </div>
      <div className="bg-card border border-border/60 rounded-lg px-7 py-6 min-h-[60px]">
        {partialText.length === 0 ? (
          <div className="flex items-center gap-2 text-text3 text-sm">
            <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
            <span>{orchestrationPhase === 'thinking' ? 'Picking the right brains and model…' : orchestrationPhase === 'selected' ? 'Reading the brains…' : 'Thinking…'}</span>
          </div>
        ) : (
          <StreamingMarkdown text={partialText} brains={brains} isActive={orchestrationPhase === 'generating' || partialText.length === 0} />
        )}
      </div>
    </div>
  );
}

function OrchestrationIndicator({
  phase, brainCount, modelShort, reason,
}: {
  phase: 'idle' | 'thinking' | 'selected' | 'generating';
  brainCount: number;
  modelShort: string | null;
  reason: string;
}) {
  if (phase === 'idle') return null;

  if (phase === 'thinking') {
    return (
      <span className="inline-flex items-center gap-1.5 bg-card border border-hairline px-2.5 py-0.5 rounded-full text-[11px] text-text2">
        <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
        Picking brains and model…
      </span>
    );
  }

  // selected or generating — show settled choices with checkmarks
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span className="inline-flex items-center gap-1.5 bg-card border border-hairline px-2.5 py-0.5 rounded-full text-[11px] text-text2">
        <CheckIcon />
        {brainCount} brain{brainCount === 1 ? '' : 's'}
      </span>
      {modelShort && (
        <span className="inline-flex items-center gap-1.5 bg-card border border-hairline px-2.5 py-0.5 rounded-full text-[11px] text-text2">
          <CheckIcon />
          {modelShort}
        </span>
      )}
      <span className="inline-flex items-center gap-1.5 bg-card border border-hairline px-2.5 py-0.5 rounded-full text-[11px] text-text2">
        <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
        {phase === 'generating' ? 'Generating' : 'Loading…'}
      </span>
      {reason && (
        <span className="text-[11px] text-text3 italic ml-1 hidden md:inline">{reason}</span>
      )}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-accent">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="mt-4 px-4 py-3 bg-card border border-accent/40 rounded text-sm text-ink">
      <div className="text-[11px] text-accent font-medium mb-1">Stream error</div>
      {message}
    </div>
  );
}

function formatClockTime(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  return `${day} · ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

// Read the persisted lens choice for a given thread, falling back to its default.
function readLensForThread(thread: Thread): 'objective' | 'lens' {
  try {
    const stored = localStorage.getItem(`element-iq:lens:${thread.id}`);
    if (stored === 'objective' || stored === 'lens') return stored;
  } catch {
    // ignore
  }
  return thread.lens_default;
}

// Lightweight skeleton card used during the initial chat load.
function SkeletonCard({ variant, widthPct = 100 }: { variant: 'user' | 'assistant'; widthPct?: number }) {
  if (variant === 'user') {
    return (
      <div className="bg-card border border-border/60 rounded-lg px-5 py-4">
        <div className="h-3 w-16 bg-bgsoft rounded mb-3 animate-pulse" />
        <div className="h-3.5 w-full bg-bgsoft rounded mb-2 animate-pulse" style={{ width: `${widthPct}%` }} />
        <div className="h-3.5 w-5/6 bg-bgsoft rounded animate-pulse" />
      </div>
    );
  }
  return (
    <div>
      <div className="h-3 w-20 bg-bgsoft rounded mb-3 animate-pulse" />
      <div className="bg-card border border-border/60 rounded-lg px-7 py-6">
        <div className="h-4 w-full bg-bgsoft rounded mb-2.5 animate-pulse" />
        <div className="h-4 w-11/12 bg-bgsoft rounded mb-2.5 animate-pulse" />
        <div className="h-4 w-4/5 bg-bgsoft rounded mb-2.5 animate-pulse" />
        <div className="h-4 w-2/3 bg-bgsoft rounded animate-pulse" />
      </div>
    </div>
  );
}
